import { useEffect } from 'react';

interface SEOProps {
  title: string;
  description: string;
  keywords: string;
  canonical: string;
  ogImage?: string;
  schema?: object;
}

export function SEO({ title, description, keywords, canonical, ogImage, schema }: SEOProps) {
  useEffect(() => {
    // Update title
    document.title = title;

    // Update or create meta tags
    const updateMetaTag = (name: string, content: string, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      let element = document.querySelector(`meta[${attribute}="${name}"]`);
      
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      
      element.setAttribute('content', content);
    };

    // Basic meta tags
    updateMetaTag('description', description);
    updateMetaTag('keywords', keywords);
    updateMetaTag('last-modified', new Date().toISOString().split('T')[0]);

    // Open Graph tags
    updateMetaTag('og:title', title, true);
    updateMetaTag('og:description', description, true);
    updateMetaTag('og:url', canonical, true);
    updateMetaTag('og:type', 'website', true);
    if (ogImage) {
      updateMetaTag('og:image', ogImage, true);
    }

    // Twitter tags
    updateMetaTag('twitter:title', title, true);
    updateMetaTag('twitter:description', description, true);
    updateMetaTag('twitter:url', canonical, true);
    updateMetaTag('twitter:card', 'summary_large_image', true);
    if (ogImage) {
      updateMetaTag('twitter:image', ogImage, true);
    }

    // Update canonical link
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute('href', canonical);

    // Add Schema.org JSON-LD
    if (schema) {
      let schemaScript = document.querySelector('script[type="application/ld+json"]');
      if (!schemaScript) {
        schemaScript = document.createElement('script');
        schemaScript.setAttribute('type', 'application/ld+json');
        document.head.appendChild(schemaScript);
      }
      schemaScript.textContent = JSON.stringify(schema);
    }
  }, [title, description, keywords, canonical, ogImage, schema]);

  return null;
}

// Schema.org generators
export const generateOrganizationSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'Seaside Vouchers',
  url: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}`,
  logo: 'https://seasidevouchers.co.uk/wp-content/uploads/2024/11/Group-1000005662-1024x974.png',
  description: 'Free app providing exclusive discounts and offers for the Lincolnshire Coast',
  address: {
    '@type': 'PostalAddress',
    addressRegion: 'Lincolnshire',
    addressCountry: 'GB'
  },
  sameAs: [
    'https://www.facebook.com/people/seasidevouchers/61566031950810/',
    'https://www.instagram.com/seaside_vouchers/'
  ],
  contactPoint: {
    '@type': 'ContactPoint',
    email: 'hello@seasidevouchers.co.uk',
    contactType: 'Customer Service'
  }
});

export const generateWebPageSchema = (name: string, description: string, url: string) => ({
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name,
  description,
  url,
  publisher: {
    '@type': 'Organization',
    name: 'Seaside Vouchers',
    logo: {
      '@type': 'ImageObject',
      url: 'https://seasidevouchers.co.uk/wp-content/uploads/2024/11/Group-1000005662-1024x974.png'
    }
  }
});

export const generateFAQSchema = (faqs: Array<{ question: string; answer: string }>) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map(faq => ({
    '@type': 'Question',
    name: faq.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: faq.answer
    }
  }))
});

export const generateProductSchema = (product: {
  name: string;
  description: string;
  image: string;
  offers: { price: number; priceCurrency: string };
}) => ({
  '@context': 'https://schema.org',
  '@type': 'Product',
  name: product.name,
  description: product.description,
  image: product.image,
  offers: {
    '@type': 'Offer',
    price: product.offers.price,
    priceCurrency: product.offers.priceCurrency,
    availability: 'https://schema.org/InStock'
  }
});

export const generateSoftwareApplicationSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'SoftwareApplication',
  name: 'Seaside Vouchers',
  operatingSystem: 'iOS, Android',
  applicationCategory: 'LifestyleApplication',
  offers: {
    '@type': 'Offer',
    price: '0',
    priceCurrency: 'GBP'
  },
  aggregateRating: {
    '@type': 'AggregateRating',
    ratingValue: '4.8',
    ratingCount: '500'
  }
});
